import React, { useEffect, useState } from 'react';
import { useContactSharing } from '../hooks/useContactSharing';
import { ShareContactData, formatContactText } from '../utils/contactShareUtils';
import { loadSharePreferences } from '../utils/sharePrefs';
import { useGroupContext } from '../lib/groups/context';
import { createContactShare } from '../lib/shares';
import QRCode from 'react-qr-code';
import RoundedQRCode from './RoundedQRCode';
import { downloadQrPng, shareQrPng } from '../utils/qr';
import { supabase } from '../lib/supabase';

interface UserProfileLite { id: string; first_name?: string | null; last_name?: string | null; display_name?: string | null; }

interface ShareModalProps {
    formData: ShareContactData;
    onClose: () => void;
    onNotification: (message: string, type?: 'success' | 'error') => void;
}

export default function ShareModal({ formData, onClose, onNotification }: ShareModalProps) {
    const { activeGroupId } = useGroupContext();
    const [showPreview, setShowPreview] = useState(true);
    const [sharing, setSharing] = useState(false);
    const [shareToken, setShareToken] = useState<string | null>(null);
    const { shareAsContactCard, shareAsVCard, copyContactInfo, shareContactNatively } = useContactSharing({
        formData,
        onNotification,
        onClose
    });

    const prefs = loadSharePreferences(activeGroupId);
    const [senderName, setSenderName] = useState<string | null>(null);
    const [meetingLocation, setMeetingLocation] = useState<string | null>(null);

    // Lightweight fetch of current user profile for a friendly sender name.
    useEffect(() => {
        (async () => {
            try {
                const session = await supabase.auth.getSession();
                const uid = session.data.session?.user?.id;
                if (!uid) return;
                const { data, error } = await supabase
                    .from('profiles')
                    .select('id, first_name, last_name, display_name')
                    .eq('id', uid)
                    .maybeSingle();
                if (!error && data) {
                    const prof = data as UserProfileLite;
                    const composed = (prof.display_name || `${prof.first_name || ''} ${prof.last_name || ''}`.trim()).trim();
                    if (composed) setSenderName(composed);
                }
            } catch { /* ignore */ }
        })();
    }, []);

    // Derive a meeting location label if raw lat / lng present in formData (avoid showing raw coordinates to recipient)
    useEffect(() => {
        try {
            const anyForm: any = formData as any;
            // Prefer existing structured fields if they exist
            const locCandidates = [
                anyForm.city && anyForm.state && `${anyForm.city}, ${anyForm.state}`,
                anyForm.city && anyForm.country && `${anyForm.city}, ${anyForm.country}`,
                anyForm.location_label,
            ].filter(Boolean) as string[];
            if (locCandidates.length) {
                setMeetingLocation(locCandidates[0]);
                return;
            }
            // If only raw lat/lng exist, suppress for privacy instead of exposing numbers
            setMeetingLocation(null);
        } catch { setMeetingLocation(null); }
    }, [formData]);

    const contactText = formatContactText(formData, prefs, { senderName, meetingLocationLabel: meetingLocation });
    const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
    const [qrFullscreen, setQrFullscreen] = useState(false);

    // Close on Escape key
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <>
            <div
                className="fixed inset-0 bg-black bg-opacity-70 z-[150] flex items-center justify-center p-4"
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div
                    className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-md w-full mx-4 relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-700">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-light text-white tracking-wide">Share Contact</h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-200 transition-colors p-1 hover:bg-gray-700 rounded"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">Choose how you'd like to share this contact</p>
                    </div>

                    {/* Removed explicit preview toggle per product direction */}

                    {/* Preview Section */}
                    {showPreview && (
                        <div className="px-6 py-4 border-b border-gray-700 bg-gray-800">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Contact Information Preview:</h4>
                            <div className="bg-gray-900 rounded p-3 text-xs text-gray-200 font-mono whitespace-pre-wrap max-h-32 overflow-y-auto border border-gray-700">
                                {contactText}
                            </div>
                            {senderName && (
                                <div className="mt-2 text-[11px] text-gray-400">Personalized with your name: {senderName}</div>
                            )}
                            {/* Public link + QR */}
                            {(() => {
                                const contactId = (formData as any)?.id as string | undefined;
                                if (!contactId) {
                                    return (
                                        <div className="mt-3 text-[11px] text-gray-400">
                                            To generate a public link, save this contact first.
                                        </div>
                                    );
                                }
                                const url = shareToken ? `${window.location.origin}/connect/v/${shareToken}` : '';
                                return (
                                    <div className="mt-3">
                                        {!shareToken ? (
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        setSharing(true);
                                                        const t = await createContactShare(contactId, 7);
                                                        setShareToken(t);
                                                        onNotification('Share link created (expires in 7 days)', 'success');
                                                    } catch (e) {
                                                        console.error('Failed to create share link', e);
                                                        onNotification('Failed to create share link', 'error');
                                                    } finally {
                                                        setSharing(false);
                                                    }
                                                }}
                                                disabled={sharing}
                                                className="px-3 py-2 rounded bg-gray-900 border border-gray-700 text-gray-200 text-sm hover:bg-gray-700 disabled:opacity-50"
                                            >
                                                {sharing ? 'Creating…' : 'Create public link (7 days)'}
                                            </button>
                                        ) : (
                                            <div className="bg-gray-900 border border-gray-700 rounded p-3">
                                                <div className="text-xs text-gray-400 mb-1">Public link (expires in 7 days)</div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="px-2 py-1 rounded bg-black/40 border border-gray-800 text-[11px] text-gray-300 truncate">{url}</div>
                                                    </div>
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(url).catch(() => { })}
                                                        className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs"
                                                    >
                                                        Copy
                                                    </button>
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs"
                                                    >
                                                        Open
                                                    </a>
                                                    <button
                                                        onClick={async () => { await shareQrPng(url, `QR for ${contactName}`, url); }}
                                                        className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs"
                                                    >
                                                        Share QR
                                                    </button>
                                                    <button
                                                        onClick={async () => { await downloadQrPng(url, `${contactName.replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`); }}
                                                        className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs"
                                                    >
                                                        Download QR
                                                    </button>
                                                </div>
                                                <div className="mt-3 w-full flex justify-center">
                                                    <button type="button" onClick={() => setQrFullscreen(true)} className="p-2 rounded bg-black/40 border border-gray-800 hover:bg-black/60">
                                                        <RoundedQRCode value={url} size={128} background="#111827" foreground="#e5e7eb" />
                                                        <div className="mt-1 text-[10px] text-gray-500">Tap to enlarge</div>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Share Options */}
                    <div className="p-6 space-y-3">
                        {/* Quick Share: click row opens share; right icon copies preview text */}
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => { shareContactNatively(); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); shareContactNatively(); } }}
                            className="w-full flex items-center p-4 bg-gray-900 border border-gray-700 text-white rounded hover:bg-gray-800 transition-all duration-200 cursor-pointer"
                        >
                            <div className="w-10 h-10 bg-gray-800 border border-gray-600 rounded flex items-center justify-center mr-3">
                                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                            </div>
                            <div className="text-left">
                                <h4 className="font-medium text-white">Quick Share</h4>
                                <p className="text-sm text-gray-400">Includes personalized intro{meetingLocation ? ' & location' : ''}.</p>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyContactInfo();
                                    onNotification('Preview copied to clipboard', 'success');
                                }}
                                className="ml-auto p-2 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700"
                                aria-label="Copy preview to clipboard"
                                title="Copy preview"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </button>
                        </div>

                        {/* Share Preview action removed per request */}

                        {/* vCard File */}
                        <button
                            onClick={() => {
                                console.log('📂 vCard File clicked');
                                shareAsVCard();
                            }}
                            className="w-full flex items-center p-4 bg-gray-900 border border-gray-700 text-white hover:bg-gray-800 transition-all duration-200"
                        >
                            <div className="w-10 h-10 bg-gray-800 border border-gray-600 flex items-center justify-center mr-3">
                                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v2a1 1 0 01-1 1h-1v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8H3a1 1 0 01-1-1V5a1 1 0 011-1h4zm2-1v1h6V3H9z" />
                                </svg>
                            </div>
                            <div className="text-left">
                                <h4 className="font-medium text-white">vCard</h4>
                                <p className="text-sm text-gray-400">Share vCard (.vcf) file</p>
                            </div>
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-700">
                        <p className="text-xs text-gray-400 text-center">
                            Share contact information quickly and professionally
                        </p>
                    </div>
                </div>
            </div>
            {qrFullscreen && shareToken && (
                <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center" onClick={() => setQrFullscreen(false)}>
                    <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 bg-black border border-gray-800">
                            <RoundedQRCode value={`${window.location.origin}/connect/v/${shareToken}`} size={360} background="#000000" foreground="#FFFFFF" />
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                            <button onClick={() => setQrFullscreen(false)} className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">Close</button>
                            <button onClick={async () => { const url = `${window.location.origin}/connect/v/${shareToken}`; await shareQrPng(url, `QR for ${contactName}`, url); }} className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">Share Image</button>
                            <button onClick={async () => { const url = `${window.location.origin}/connect/v/${shareToken}`; await downloadQrPng(url, `${contactName.replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`); }} className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">Download</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
